import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
    useEditSkillFrameworkMutation,
    useGetOrgSkillFrameworkTypesQuery,
    useGetPublicSkillFrameworkTypesQuery,
} from '@/state/skills/skills-api';
import type { ApiErrorResponse } from '@/types/async-boundary.types';
import { getMessageByStatus } from '@/lib/error-message';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import { normalizeSkillDescription } from './skill-framework-chart-utils';

const editSkillFrameworkSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    skill_type: z.string().min(1, 'Skill type is required'),
    parent: z.string().optional(),
});

type FormValues = z.infer<typeof editSkillFrameworkSchema>;

interface ParentOption {
    id: string;
    name: string;
}

interface EditSkillFrameworkFormProps {
    tenant: string;
    isOpen: boolean;
    onClose: () => void;
    nodeId: string | number;
    rootId: string | number;
    nodeName: string;
    nodeDescription: string;
    nodeSkillTypeId: number | null;
    currentParentId: string | number | null;
    parentOptions: ParentOption[];
    isRoot: boolean;
    onUpdated?: () => void;
}

const EditSkillFrameworkForm = ({
    tenant,
    isOpen,
    onClose,
    nodeId,
    rootId,
    nodeName,
    nodeDescription,
    nodeSkillTypeId,
    currentParentId,
    parentOptions,
    isRoot,
    onUpdated,
}: EditSkillFrameworkFormProps) => {
    const { userRoles } = useClientPermissions();
    const isSuperAdmin = userRoles.includes('SUPER ADMIN');
    const dialogTitle = isRoot ? 'Edit Skill Framework' : 'Edit Skill';
    const dialogDescription = isRoot
        ? 'Update the skill framework details'
        : 'Update this skill details';
    
    const [description, setDescription] = useState<string>('');

    const form = useForm<FormValues>({
        resolver: zodResolver(editSkillFrameworkSchema),
        defaultValues: {
            name: nodeName,
            skill_type:
                nodeSkillTypeId === null ? 'Other' : String(nodeSkillTypeId),
            parent: currentParentId ? String(currentParentId) : '',
        },
    });

    const [editSkillFramework, { isLoading: isEditing }] =
        useEditSkillFrameworkMutation();
    const { data: orgSkillTypes = [], isLoading: isOrgTypesLoading } =
        useGetOrgSkillFrameworkTypesQuery(
            { tenant },
            { skip: !tenant || isSuperAdmin }
        );
    const { data: publicSkillTypes = [], isLoading: isPublicTypesLoading } =
        useGetPublicSkillFrameworkTypesQuery(
            { tenant },
            { skip: !tenant || !isSuperAdmin }
        );

    const skillTypes = isSuperAdmin ? publicSkillTypes : orgSkillTypes;
    const isTypesLoading = isSuperAdmin ? isPublicTypesLoading : isOrgTypesLoading;
    const skillTypeOptions = [
        ...skillTypes.map((type) => ({
            id: String(type.id),
            name: type.name,
        })),
        { id: 'Other', name: 'Other' },
    ];

    const onSubmit = async (data: FormValues) => {
        const nextParent =
            !isRoot && data.parent && data.parent.length > 0
                ? Number(data.parent)
                : undefined;
        const parentChanged =
            !isRoot &&
            String(currentParentId ?? '') !== String(nextParent ?? '');

        try {
            await editSkillFramework({
                tenant,
                id: nodeId,
                rootId,
                name: data.name,
                skill_type:
                    data.skill_type === 'Other'
                        ? null
                        : parseInt(data.skill_type),
                description: description,
                parent: parentChanged ? nextParent ?? null : undefined,
            }).unwrap();

            toast.success('Skill updated', {
                description: 'The skill has been updated successfully',
            });

            form.reset();
            setDescription('');
            onClose();
            onUpdated?.();
        } catch (err) {
            const error = err as ApiErrorResponse;
            toast.error('Error updating skill', {
                description: getMessageByStatus(error?.status || 500),
            });
        }
    };

    const handleClose = () => {
        if (!isEditing) {
            form.reset({
                name: nodeName,
                skill_type:
                    nodeSkillTypeId === null ? 'Other' : String(nodeSkillTypeId),
                parent: currentParentId ? String(currentParentId) : '',
            });
            setDescription(normalizeSkillDescription(nodeDescription));
            onClose();
        }
    };

    useEffect(() => {
        if (!isOpen) return;
        form.reset({
            name: nodeName,
            skill_type:
                nodeSkillTypeId === null ? 'Other' : String(nodeSkillTypeId),
            parent: currentParentId ? String(currentParentId) : '',
        });
        setDescription(normalizeSkillDescription(nodeDescription));
    }, [isOpen, nodeName, nodeSkillTypeId, currentParentId, nodeDescription, form]);


    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[560px]">
                <DialogHeader className="mb-4">
                    <DialogTitle>{dialogTitle}</DialogTitle>
                    <DialogDescription>{dialogDescription}</DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-6"
                    >
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Name</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Enter name" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="skill_type"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Skill Type</FormLabel>
                                    <FormControl>
                                        <Select
                                            value={field.value}
                                            onValueChange={field.onChange}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue
                                                    placeholder={
                                                        isTypesLoading
                                                            ? 'Loading skill types...'
                                                            : 'Select skill type'
                                                    }
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {skillTypeOptions.map((type) => (
                                                    <SelectItem
                                                        key={type.id}
                                                        value={type.id}
                                                    >
                                                        {type.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                                <div className="space-y-1">
                                    <Textarea
                                        placeholder="Add a description for this skill..."
                                        value={description}
                                        onChange={(e) => {
                                            if (e.target.value.length <= 1000) {
                                                setDescription(e.target.value);
                                            }
                                        }}
                                        className="resize-none"
                                        rows={4}
                                    />
                                    <div className="text-xs text-muted-foreground text-right">
                                        {description.length}/1000
                                    </div>
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>

                        {!isRoot && (
                            <FormField
                                control={form.control}
                                name="parent"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Move to</FormLabel>
                                        <FormControl>
                                            <Select
                                                value={field.value}
                                                onValueChange={field.onChange}
                                            >
                                                <SelectTrigger className="w-full">
                                                    <SelectValue placeholder="Select parent" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {parentOptions.map((parent) => (
                                                        <SelectItem
                                                            key={parent.id}
                                                            value={parent.id}
                                                        >
                                                            {parent.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}

                        <div className="flex justify-end gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleClose}
                                disabled={isEditing}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isEditing}>
                                {isEditing ? 'Saving...' : 'Save'}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default EditSkillFrameworkForm;
